// Helpers for copying a single sandbox config record into another sandbox (see CopyToModal).

// Fields owned by the source sandbox that must never be sent to the destination sandbox.
// sandbox_id matters most: apiRequest fills URL placeholders from the request body before its replacements, so a
// body that still carried the source sandbox_id would send the copy's PUT back to the source sandbox.
const SOURCE_SANDBOX_KEYS = ["sandbox_id", "last_modified_account_id", "last_modified_timestamp", "created_timestamp"];

/**
 * An error from one step of a copy, with a title saying which step failed and the underlying apiRequest error.
 */
export class CopyStepError extends Error {
  constructor(title, cause) {
    super(title);
    this.title = title;
    this.cause = cause;
  }
}

/**
 * Find an endpoint from the page's definitions.json endpoint list.
 * @param {Object} pageData
 * @param {"get"|"put"} method
 * @param {boolean} single - true for the single-resource path (ending in "/{id}"), false for the collection path
 */
export const findCopyEndpoint = (pageData, method, single) =>
  pageData?.Endpoints?.find((e) => e.method === method && /\/\{[^/]+\}$/.test(e.path) === single);

/**
 * Build the PUT body used to copy a record into another sandbox.
 * The record must be the raw API record (not a table/form row), so every field, including child collections such as
 * XP thresholds or price breakpoints, is copied as stored. Only fields owned by the source sandbox are dropped.
 */
export const buildCopyPayload = (sourceRecord) =>
  Object.fromEntries(Object.entries(sourceRecord || {}).filter(([key]) => !SOURCE_SANDBOX_KEYS.includes(key)));

const isNotFound = (error) => error?.response?.status === 404;

/**
 * Read the record from the source sandbox and look it up in the destination sandbox.
 *
 * Only a 404 means the record is not in the destination; any other failure stops the copy, because the destination PUT
 * upserts and would otherwise silently overwrite a record we could not see.
 *
 * @param {Object} options
 * @param {Function} options.request - (endpoint, replacements, queryParams?) => Promise of the response body
 * @param {Object} options.pageData - RHDynamicPage page data (RowKey, LegacyRowKey, Endpoints, TableDataArrayName)
 * @param {string} options.sourceSandboxId
 * @param {string} options.targetSandboxId
 * @param {string} options.objectId - the record's primary ID
 * @returns {Promise<{sourceRecord: Object, existing: Object|null, legacyConflict: Object|null, putEndpoint: Object}>}
 *   existing is the destination record with the same ID (the copy updates it); legacyConflict is a different
 *   destination record that already holds the source's legacy ID (the API rejects the copy).
 */
export const prepareCopy = async ({ request, pageData, sourceSandboxId, targetSandboxId, objectId }) => {
  const getOneEndpoint = findCopyEndpoint(pageData, "get", true);
  const getManyEndpoint = findCopyEndpoint(pageData, "get", false);
  const putEndpoint = findCopyEndpoint(pageData, "put", true);
  const idKey = pageData?.RowKey;
  const legacyIdKey = pageData?.LegacyRowKey;
  if (!getOneEndpoint || !putEndpoint || !idKey || (legacyIdKey && !getManyEndpoint)) {
    throw new CopyStepError("Copy is not available for this page");
  }

  let sourceRecord;
  try {
    // Read the record fresh: the table row is rebuilt from the form columns, so it lacks hidden fields and can be stale
    sourceRecord = await request(getOneEndpoint, { sandbox_id: sourceSandboxId, [idKey]: objectId });
  } catch (error) {
    throw new CopyStepError("Could not read the source record", error);
  }

  let existing = null;
  try {
    existing = await request(getOneEndpoint, { sandbox_id: targetSandboxId, [idKey]: objectId });
  } catch (error) {
    if (!isNotFound(error)) {
      throw new CopyStepError("Could not check the destination sandbox", error);
    }
  }

  let legacyConflict = null;
  const legacyId = legacyIdKey ? sourceRecord?.[legacyIdKey] : null;
  if (!existing && legacyId) {
    try {
      const response = await request(getManyEndpoint, { sandbox_id: targetSandboxId }, `${legacyIdKey}s=${legacyId}`);
      const records = response?.[pageData.TableDataArrayName || "data"] || [];
      legacyConflict = records.find((record) => record[legacyIdKey] === legacyId && record[idKey] !== objectId) || null;
    } catch (error) {
      throw new CopyStepError("Could not check the destination sandbox", error);
    }
  }

  return { sourceRecord, existing, legacyConflict, putEndpoint };
};
